// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { buildEvent } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { getCommodityId, INVESTMENT_ACCOUNT_TYPES } from '../../lib/investment.js';
import type { AccountCreatedPayload, AccountType, Commodity } from '../../types.js';
import { v4 as uuidv4 } from 'uuid';

const EXCHANGES = ['NYSE', 'NASDAQ', 'AMEX', 'MUTFUND', 'CRYPTO', 'CURRENCY', 'OTHER'];

const inputCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none';
const labelCls = 'block text-xs text-text-muted mb-1';

interface AddSecurityDialogProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  onClose: () => void;
}

function AddSecurityDialog({ worker, keyData, onClose }: AddSecurityDialogProps) {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);
  const navigate = useNavigate();

  const [ticker, setTicker] = useState('');
  const [fullName, setFullName] = useState('');
  const [exchange, setExchange] = useState('NYSE');
  const [accountName, setAccountName] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const accounts = useMemo(() => [...ledger.accounts.values()].filter((a) => a.placeholder || a.type === 'ASSET'), [ledger.accounts]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!ticker.trim()) { setError(t('validation.tickerRequired')); return; }
    if (!fullName.trim()) { setError(t('validation.fullNameRequired')); return; }
    if (!accountName.trim()) { setError(t('validation.accountNameRequired')); return; }

    const type: AccountType = exchange === 'MUTFUND' ? 'MUTUAL_FUND' : 'STOCK';
    const commodity: Commodity = {
      ticker: ticker.toUpperCase().trim(),
      exchange,
      fullName: fullName.trim(),
    };

    setSaving(true);
    try {
      const payload: AccountCreatedPayload = {
        accountId: uuidv4(),
        name: accountName.trim(),
        type,
        currency: 'USD',
        parentId: parentId || null,
        description: fullName.trim(),
        placeholder: false,
        commodity,
      };
      await encryptAndPush(await buildEvent('AccountCreated', payload, ledger.lastSequence + 1));
      onClose();
      navigate(`/accounts/${payload.accountId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('commodities.createFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-border w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-md font-semibold text-text">{t('commodities.addSecurity')}</h2>
            <button onClick={onClose} className="text-text-muted hover:text-text text-xl leading-none">&times;</button>
          </div>

          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>{t('commodities.tickerSymbol')}</label>
                <input
                  type="text"
                  value={ticker}
                  onChange={(e) => setTicker(e.target.value.toUpperCase())}
                  placeholder="AAPL"
                  className={`${inputCls} font-mono`}
                  autoFocus
                />
              </div>
              <div>
                <label className={labelCls}>{t('commodities.exchange')}</label>
                <select
                  value={exchange}
                  onChange={(e) => setExchange(e.target.value)}
                  className="w-full bg-bg border border-border text-text text-sm px-2 py-2 focus:border-accent focus:outline-none"
                >
                  {EXCHANGES.map((ex) => (
                    <option key={ex} value={ex}>{ex}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>{t('commodities.fullName')}</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Apple Inc."
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>{t('commodities.accountNameInLedger')}</label>
              <input
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder={ticker || 'AAPL'}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>{t('commodities.parentAccount')} <span className="text-text-subtle">{t('optional')}</span></label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full bg-bg border border-border text-text text-sm px-2 py-2 focus:border-accent focus:outline-none"
              >
                <option value="">{t('commodities.topLevel')}</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">
                {t('common:actions.cancel')}
              </button>
              <button type="submit" disabled={saving} className="flex-1 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors">
                {saving ? t('creating') : t('common:actions.create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

interface CommodityManagerProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

export function CommodityManager({ worker, keyData }: CommodityManagerProps) {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);

  const investmentAccounts = useMemo(
    () => [...ledger.accounts.values()].filter((a) => INVESTMENT_ACCOUNT_TYPES.has(a.type) && !a.placeholder),
    [ledger.accounts],
  );

  // Group accounts by ticker (commodity)
  const commodities = useMemo(() => {
    const map = new Map<string, { ticker: string; fullName: string; exchange: string; accounts: typeof investmentAccounts }>();
    for (const account of investmentAccounts) {
      const id = getCommodityId(account);
      const existing = map.get(id);
      if (existing) {
        existing.accounts.push(account);
      } else {
        map.set(id, {
          ticker: account.commodity?.ticker ?? id,
          fullName: account.commodity?.fullName ?? account.name,
          exchange: account.commodity?.exchange ?? '—',
          accounts: [account],
        });
      }
    }
    return [...map.entries()].map(([id, info]) => ({ id, ...info }));
  }, [investmentAccounts]);

  return (
    <div className="flex-1 overflow-auto">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-bg-surface">
        <h2 className="text-md font-semibold text-text">{t('commodities.title')}</h2>
        <button
          onClick={() => setShowAdd(true)}
          className="ml-auto px-3 py-1 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          {t('commodities.addSecurity')}
        </button>
      </div>

      {commodities.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-text-muted">
          <div className="text-center">
            <p className="text-sm">{t('commodities.noSecurities')}</p>
            <button onClick={() => setShowAdd(true)} className="mt-1 text-accent hover:text-accent-hover text-sm">
              {t('commodities.addFirst')}
            </button>
          </div>
        </div>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead className="bg-bg-surface border-b border-border-strong sticky top-0 z-10">
            <tr>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('commodities.columns.symbol')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('commodities.columns.name')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('commodities.exchange')}</th>
              <th className="text-center text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('commodities.columns.accounts')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {commodities.map((c, idx) => (
              <tr
                key={c.id}
                className={`border-b border-border cursor-pointer hover:bg-bg-elevated transition-colors ${idx % 2 === 0 ? 'bg-bg' : 'bg-bg-surface'}`}
                onClick={() => navigate(`/settings/commodities/${encodeURIComponent(c.id)}/prices`)}
              >
                <td className="py-2 px-4 font-mono font-medium text-text">{c.ticker}</td>
                <td className="py-2 px-4 text-text-muted">{c.fullName}</td>
                <td className="py-2 px-4 text-text-subtle text-xs">{c.exchange}</td>
                <td className="py-2 px-4 text-center text-text-muted">{c.accounts.length}</td>
                <td className="py-2 px-4 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/settings/commodities/${encodeURIComponent(c.id)}/prices`); }}
                    className="text-xs text-accent hover:text-accent-hover"
                  >
                    {t('commodities.pricesLink')} →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddSecurityDialog
          worker={worker}
          keyData={keyData}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}
